import React, { useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  CardFooter,
  Collapse,
} from "reactstrap"

// Formik Validation
import * as Yup from "yup"
import { useFormik } from "formik"

//redux
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"

import withRouter from "../../components/Common/withRouter"

//Import Breadcrumb
import Breadcrumb from "../../components/Common/Breadcrumb"

import avatar from "../../assets/images/users/avatar-9.jpg"
import { Link, useNavigate } from "react-router-dom"
import PlanCards from "./PlansComponent"
import "./userProfile.css"
import debit from "../../assets/images/debitcard.png"
import paypal from "../../assets/images/paypal.png"
// actions
import { userProfileApi } from "../../services/userProfileApi"
import SkeletonCard from "../KeywordTracking/components/SkeletonCard"
import profile from "../../assets/images/profile.png"

const UserProfile = props => {
  document.title = " Profile | Dashboard"

  const dispatch = useDispatch()
  const navigate = useNavigate()

  // const [email, setemail] = useState("")
  const [name, setname] = useState("")
  const [idx, setidx] = useState("")
  const [visible, setVisible] = useState(false)
  const [storedUsername, setStoredUsername] = useState(localStorage.getItem("userName") || "")
  const [email, setEmail] = useState(localStorage.getItem("userEmail") || "")
  const [isOpenPaymentinfo, setIsOpenPaymentinfo] = useState(false)
  const togglePaymentinfo = () => setIsOpenPaymentinfo(!isOpenPaymentinfo)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("")
  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method)
  }

  // Billing History state
  const [billing, setBilling] = useState({ loading: true, error: null, items: [] })
  const [invoiceLoading, setInvoiceLoading] = useState({})
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState(null)
  const [currentPlanName, setCurrentPlanName] = useState(null)
  const [currentPlanLoading, setCurrentPlanLoading] = useState(true)
  const [isCancelling, setIsCancelling] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelPlanData, setCancelPlanData] = useState(null)

  // Fetch profile details and hydrate local UI
  useEffect(() => {
    const fetchProfileDetails = async () => {
      try {
        const details = await userProfileApi.fetchProfileDetails()
        if (details?.name) {
          setStoredUsername(details.name)
          localStorage.setItem('userName', details.name)
        }
        if (details?.email) {
          setEmail(details.email)
          localStorage.setItem('userEmail', details.email)
        }
      } catch (e) {
        // non-blocking; render whatever is available
        console.error('Failed to fetch profile details', e)
      }
    }
    fetchProfileDetails()
  }, [])

  const handleEditProfile = () => {
    navigate("/profile/settings")
  }

  // Fetch Billing History
  useEffect(() => {
    const fetchBilling = async () => {
      try {
        setBilling(prev => ({ ...prev, loading: true, error: null }))

        const result = await userProfileApi.fetchBillingHistory(1)
        const items = Array.isArray(result?.data) ? result.data : []
        setBilling({ loading: false, error: null, items })
      } catch (e) {
        setBilling({ loading: false, error: e.message || "Failed to load", items: [] })
      }
    }
    fetchBilling()
  }, [])

  // Fetch current subscription/plan
  useEffect(() => {
    const fetchCurrent = async () => {
      try {
        setCurrentPlanLoading(true)
        const res = await userProfileApi.fetchCurrentPlan()
        if (res && res.data) {
          const subId = res.data.subscription_id || res.data.id || null
          const planId = res.data.product || null
          const planName = res.data.product_name || res.data.plan_name || null
          const status = res.data.status || res.data.subscription_status || null

          // Determine whether subscription appears active and cancellable.
          // Treat 'cancelled_but_active' as NOT cancellable (user has access until end date,
          // but should not see the Cancel Subscription button again until a new active plan exists).
          const isActive =
            subId &&
            subId !== null &&
            status !== 'cancelled' &&
            status !== 'canceled' &&
            status !== 'cancelled_but_active'

          if (isActive) {
            setCurrentSubscriptionId(subId)
            setCurrentPlanName(planName)
          } else {
            setCurrentSubscriptionId(null)
            setCurrentPlanName(null)
          }
        }
      } catch (err) {
        console.error('Error fetching current plan in profile:', err)
        setCurrentSubscriptionId(null)
        setCurrentPlanName(null)
      } finally {
        setCurrentPlanLoading(false)
      }
    }
    fetchCurrent()
  }, [])

  // Helper to refresh billing list (used after cancellation)
  const refreshBilling = async () => {
    try {
      setBilling(prev => ({ ...prev, loading: true, error: null }))
      const result = await userProfileApi.fetchBillingHistory(1)
      const items = Array.isArray(result?.data) ? result.data : []
      setBilling({ loading: false, error: null, items })
    } catch (e) {
      setBilling({ loading: false, error: e.message || "Failed to load", items: [] })
    }
  }

  // Open the confirmation modal for cancelling subscription
  const openCancelModal = () => {
    if (!currentSubscriptionId) return
    setCancelPlanData({ subscriptionId: currentSubscriptionId, planName: currentPlanName })
    setShowCancelModal(true)
  }

  // Confirm and perform cancellation immediately
  const confirmCancelSubscription = async () => {
    const subscriptionId = (cancelPlanData && cancelPlanData.subscriptionId) || currentSubscriptionId
    if (!subscriptionId) return
    try {
      setIsCancelling(true)
      await userProfileApi.cancelSubscription(subscriptionId)
      // Clear local subscription state and refresh billing
      setCurrentSubscriptionId(null)
      setCurrentPlanName(null)
      await refreshBilling()
      setShowCancelModal(false)
      setCancelPlanData(null)
      // Optionally show toast/success message
      console.log('Subscription cancelled successfully')
    } catch (err) {
      console.error('Cancellation failed:', err)
      alert(`Failed to cancel subscription: ${err.message || err}`)
    } finally {
      setIsCancelling(false)
    }
  }

  const formatAmount = (amount, currency) => {
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(Number(amount || 0))
    } catch (_) {
      return `$${amount}`
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (_) {
      return iso
    }
  }

  const handleExportInvoice = async (paymentId) => {
    try {
      setInvoiceLoading(prev => ({ ...prev, [paymentId]: true }))
      const data = await userProfileApi.getInvoice(paymentId)

      if (data.invoice_url) {
        window.open(data.invoice_url, '_blank')
      } else {
        console.error('No invoice URL received')
      }
    } catch (error) {
      console.error('Error fetching invoice:', error)
    } finally {
      setInvoiceLoading(prev => ({ ...prev, [paymentId]: false }))
    }
  }

  return (
    <React.Fragment>
      <div className="container-fluid" style={{ padding: 32 }}>
        <div className="page-content px-0">
          <div className="container-fluid px-0">
            {/* Profile Header */}

            <Row>
              <Col lg="12">
                <div className="profile-card">
                  <div className="profile-info">
                    <div className="profile-avatar">
                      <img
                        src={profile}
                        alt="Profile"
                      />
                    </div>
                    <div className="profile-details">
                      <h5>
                        {storedUsername || ''}
                      </h5>
                      <p>
                        {email || ''}
                      </p>
                    </div>
                  </div>
                  <Button
                    className="edit-profile-btn"
                    onClick={handleEditProfile}
                  >
                    Edit Profile
                  </Button>
                </div>
              </Col>
            </Row>

            {/* <h4 className="card-title mb-4">Change User Name</h4>

            <Card>
              <CardBody>
                <Form
                  className="form-horizontal"
                  onSubmit={e => {
                    e.preventDefault()

                    return false
                  }}
                >
                  <div className="form-group">
                    <Label className="form-label">User Name</Label>
                    <Input
                      name="username"
                      // value={name}
                      className="form-control"
                      placeholder="Enter User Name"
                      type="text"
                      value=""
                    />
                    <Input name="idx" value={idx} type="hidden" />
                  </div>
                  <div className="text-center mt-4">
                    <Button type="submit" color="danger">
                      Update User Name
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card> */}

            <div className="section-header">
              <span className="section-header-icon">
                <svg width="21.33" height="20" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.9997 0.333008L14.9997 6.99967L21.6663 2.99967L20.333 14.9997H1.66634L0.333008 2.99967L6.99967 6.99967L10.9997 0.333008ZM20.333 17.6663C21.0663 17.6663 21.6663 18.2663 21.6663 18.9997C21.6663 19.733 21.0663 20.333 20.333 20.333H1.66634C0.933008 20.333 0.333008 19.733 0.333008 18.9997C0.333008 18.2663 0.933008 17.6663 1.66634 17.6663H20.333Z" fill="white" />
                </svg>
              </span>

              <h4 className="section-title">
                Plans
              </h4>
            </div>
            {/* Cancellation Confirmation Modal (centered) */}
            {showCancelModal && cancelPlanData && (
              <div className="modal-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}>
                <div className="modal-content" style={{
                  backgroundColor: '#171821',
                  border: '1px solid rgba(53, 55, 72, 1)',
                  borderRadius: '12px',
                  padding: '24px',
                  maxWidth: '420px',
                  width: '92%',
                  color: '#ffffff'
                }}>
                  <h4 style={{ marginBottom: '16px', color: '#ffffff' }}>Cancel Subscription</h4>
                  <p style={{ marginBottom: '16px', color: 'rgba(161, 172, 184, 1)' }}>
                    Are you sure you want to cancel your <strong>{cancelPlanData.planName || 'subscription'}</strong>?
                  </p>
                  <p style={{ marginBottom: '20px', color: 'rgba(161, 172, 184, 1)', fontSize: '14px' }}>
                    You will continue to have access until the end of your current billing period.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <Button
                      color="secondary"
                      onClick={() => { setShowCancelModal(false); setCancelPlanData(null); }}
                      style={{ minWidth: '90px' }}
                    >
                      Keep Plan
                    </Button>
                    <Button
                      color="danger"
                      onClick={confirmCancelSubscription}
                      disabled={isCancelling}
                      style={{ minWidth: '120px' }}
                    >
                      {isCancelling ? 'Cancelling...' : 'Cancel Plan'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <PlanCards />

            {/* Cancel subscription button will appear after Billing History */}

            {/* Payment Method Section - Hidden for now
            <div className="section-header pay_method_sec">
              <span className="section-header-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M26.6665 5.33301H5.33317C3.8665 5.33301 2.6665 6.53301 2.6665 7.99967V23.9997C2.6665 25.4663 3.8665 26.6663 5.33317 26.6663H26.6665C28.1332 26.6663 29.3332 25.4663 29.3332 23.9997V7.99967C29.3332 6.53301 28.1332 5.33301 26.6665 5.33301ZM26.6665 23.9997H5.33317V15.9997H26.6665V23.9997ZM26.6665 10.6663H5.33317V7.99967H26.6665V10.6663Z" fill="white" />
                </svg>

              </span>

              <h4 className="section-title ">
                Payment Method
              </h4>
            </div>
            <div className="payment-container">

              <div className="payment-options">
                <div
                  onClick={() => handlePaymentMethodSelect('card')}
                  className={`payment-option ${selectedPaymentMethod === 'card' ? 'selected' : ''}`}
                >
                <img src={debit} alt=""/>
                  <div className="payment-option-text">
                    Credit / Debit Card
                  </div>
                </div>

                <div
                  onClick={() => handlePaymentMethodSelect('paypal')}
                  className={`payment-option ${selectedPaymentMethod === 'paypal' ? 'selected' : ''}`}
                >
                  <img src={paypal} alt=""/>
                  <div className="payment-option-text">
                    Paypal
                  </div>
                </div>
              </div>

              <div className="proceed-button-container">
                <button
                  onClick={() => {
                    if (selectedPaymentMethod) {
                      console.log('Proceeding with:', selectedPaymentMethod)
                    }
                  }}
                  disabled={!selectedPaymentMethod}
                  className={`proceed-button ${selectedPaymentMethod ? 'enabled' : ''}`}
                >
                  Proceed
                </button>
              </div>
            </div>
            */}

            {/* Billing History Section */}
            <div className="section-header pay_method_sec">
              <span className="section-header-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.6665 2.66699V5.33366H5.33317V29.4403L9.9545 25.7443L12.9998 28.451L15.9998 25.7843L18.9998 28.451L22.0452 25.7443L26.6665 29.4403V5.33366H29.3332V2.66699H2.6665ZM21.3332 12.0003H10.6665V9.33366H21.3332V12.0003ZM11.9998 17.3337V14.667H19.9998V17.3337H11.9998Z" fill="white" />
                </svg>
              </span>

              <h4 className="section-title">
                Billing History
              </h4>
            </div>

            <div className="billing-container border">
              {/* Billing History Table */}
              <div className="billing-table-container">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>
                        <div className="bill1">Transaction ID</div>
                      </th>
                      <th>
                        <div className="bill1">Description</div>
                      </th>
                      <th>
                        <div className="bill1">Date</div>
                      </th>
                      <th>
                        <div className="bill1">Status</div>
                      </th>
                      <th>
                        <div className="bill1">Amount</div>
                      </th>
                      {/* <th>
                        <div className="bill1">Action</div>
                      </th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {billing.loading && (
                      <SkeletonCard />
                    )}
                    {!billing.loading && billing.error && (
                      <tr>
                        <td colSpan="6">{billing.error}</td>
                      </tr>
                    )}
                    {!billing.loading && !billing.error && billing.items.length === 0 && (
                      <tr>
                        <td colSpan="6">No records</td>
                      </tr>
                    )}
                    {!billing.loading && !billing.error && billing.items.map((p, idx) => (
                      <tr key={`${p.id || idx}-${p.created_at}`}>
                        <td>
                          {p.id || `${(p.gateway || 'pay')}-${idx + 1}`}
                        </td>
                        <td>
                          {`Payment via ${String(p.gateway || '').toUpperCase() || 'GATEWAY'}`}
                        </td>
                        <td>
                          {formatDate(p.created_at)}
                        </td>
                        <td>
                          <span className="status-badge">
                            {String(p.status || '').charAt(0).toUpperCase() + String(p.status || '').slice(1)}
                          </span>
                        </td>
                        <td>
                          {formatAmount(p.amount, p.currency)}
                        </td>
                        <td>
                          <Button
                            className="edit-profile-btn"
                            onClick={() => handleExportInvoice(p.id)}
                            disabled={!p.id || invoiceLoading[p.id]}
                          >
                            {invoiceLoading[p.id] ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              </>
                            ) : (
                              'Invoice'
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Centered Cancel Subscription button placed AFTER Billing History
                Only render while checking subscription or when an active subscription exists. */}
            {(currentPlanLoading || currentSubscriptionId) && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                <Button
                  className="edit-profile-btn"
                  color="danger"
                  onClick={openCancelModal}
                  disabled={!currentSubscriptionId || isCancelling}
                  style={{ minWidth: 220 }}
                >
                  {currentPlanLoading ? (
                    'Checking subscription...'
                  ) : isCancelling ? (
                    'Cancelling...'
                  ) : (
                    `Cancel ${currentPlanName || 'Subscription'}`
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default UserProfile
